// ---------------------------------------------------------------------------
// boundaryUtils.ts — shared boundary geometry helpers.
//
// Extracted from DepartmentInfoStep.tsx so they can be used in both
// the onboarding wizard and the DepartmentMappingTab.
// ---------------------------------------------------------------------------

import type { GeoJSON } from 'geojson';

export type BoundaryGeometry = GeoJSON.Polygon | GeoJSON.MultiPolygon;

export interface SelectedBoundary {
  nerisId: string;
  departmentName: string;
  departmentType: 'CAREER' | 'VOLUNTEER' | 'COMBINATION';
  geometry: BoundaryGeometry;
  centroid: [number, number];
  area: number;
  bbox: [number, number, number, number];
  verified: boolean;
  verifiedAt: string;
  verificationSources: { arcgis: boolean; platform: boolean };
}

// ---------------------------------------------------------------------------
// computeBbox — returns [minLng, minLat, maxLng, maxLat]
// ---------------------------------------------------------------------------
export function computeBbox(geometry: BoundaryGeometry): [number, number, number, number] {
  const coords: number[][] = [];
  const collect = (rings: number[][][]) => rings.forEach((r) => r.forEach((c) => coords.push(c)));
  if (geometry.type === 'Polygon') {
    collect(geometry.coordinates);
  } else {
    geometry.coordinates.forEach((p) => collect(p));
  }
  if (coords.length === 0) return [0, 0, 0, 0];
  let minLng = coords[0][0], maxLng = coords[0][0];
  let minLat = coords[0][1], maxLat = coords[0][1];
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  return [minLng, minLat, maxLng, maxLat];
}

// ---------------------------------------------------------------------------
// computeCentroid — returns [lng, lat] center of the bounding box
// ---------------------------------------------------------------------------
export function computeCentroid(geometry: BoundaryGeometry): [number, number] {
  const bbox = computeBbox(geometry);
  return [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2];
}

// ---------------------------------------------------------------------------
// computeArea — rough square-mile approximation from bbox degrees.
//
// Uses the mid-latitude cosine correction so the lng-degree→miles conversion
// is accurate for the department's actual latitude:
//   1° latitude  ≈ 69.0 miles  (constant)
//   1° longitude ≈ 69.0 * cos(avgLat) miles
//   area ≈ |lngDelta * latDelta| * 69.0² * cos(avgLat_radians)
// ---------------------------------------------------------------------------
export function computeArea(geometry: BoundaryGeometry): number {
  const bbox = computeBbox(geometry);
  const lngDelta = bbox[2] - bbox[0];
  const latDelta = bbox[3] - bbox[1];
  const avgLat = (bbox[1] + bbox[3]) / 2;
  const milesPerDegree = 69.0;
  return Math.abs(lngDelta * latDelta) * milesPerDegree * milesPerDegree * Math.cos((avgLat * Math.PI) / 180);
}

// ---------------------------------------------------------------------------
// buildSelectedBoundary — constructs a SelectedBoundary from raw NERIS data.
// Returns undefined if no geometry is provided.
// ---------------------------------------------------------------------------
export function buildSelectedBoundary(
  nerisId: string,
  departmentName: string,
  departmentType: string | undefined,
  geometry: BoundaryGeometry | null,
): SelectedBoundary | undefined {
  if (!geometry) return undefined;
  const bbox = computeBbox(geometry);
  const centroid: [number, number] = [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2];
  const area = computeArea(geometry);
  const deptTypeUpper = (departmentType?.toUpperCase() ?? 'CAREER') as 'CAREER' | 'VOLUNTEER' | 'COMBINATION';
  return {
    nerisId,
    departmentName: departmentName ?? '',
    departmentType: ['CAREER', 'VOLUNTEER', 'COMBINATION'].includes(deptTypeUpper)
      ? deptTypeUpper
      : 'CAREER',
    geometry,
    centroid,
    area,
    bbox,
    verified: true,
    verifiedAt: new Date().toISOString(),
    verificationSources: { arcgis: false, platform: true },
  };
}
